import { Component, OnInit, Output, EventEmitter, Input, ViewChild, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { debounceTime, filter, distinctUntilChanged, map, finalize, takeUntil } from 'rxjs/operators';
import { CampaignsService } from 'src/app/pages/campaign-module/campaigns/campaigns.service';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { of as observableOf, Subscription, fromEvent } from 'rxjs';
import { DialogService, LanguageService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { requestCity } from 'src/app/model/requests';
import * as _ from 'lodash';

@Component({
  selector: 'esc-city-dropdown',
  templateUrl: './city-dropdown.component.html',
  styleUrls: ['./city-dropdown.component.scss']
})
export class CityDropdownComponent implements OnInit {
  @Input() wrapperLayoutAlign;
  @Input() formWrapperFlex;
  @Input() previousData;
  @Input() requiredError;
  @Input() isRequired;
  @Input() disabled;
  @Input() cascadeFilters;
  @Input() displayDescOnly: boolean = false;
  @Output() optionSelected = new EventEmitter;
  @Output() errorLoading = new EventEmitter;
  
  @ViewChild('cityAC') cityACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) cityACTr: MatAutocompleteTrigger;

  form: FormGroup;
  loading = {
    city: false,
    init: false,
    more: false,
    error: false
  };
  public listData = [];
  private currentFilters;
  private listSub: Subscription;
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    private fb: FormBuilder,
    public campSvc: CampaignsService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    ) { }

  ngOnInit() {
    this.currentFilters = _.cloneDeep(requestCity);
    this.initForm();
    this.detectFormChanges();
    // console.log(this.previousData);
  }

  ngOnDestroy() {
    if (this.listSub && !this.listSub.closed) {
      this.listSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.form && _.get(changes, 'requiredError') && changes.requiredError.currentValue) {
      this.form.get('city').markAsTouched();
    }
    
    if (this.form && _.get(changes, 'disabled') && changes.disabled.currentValue) {
      this.form.disable()
    }
    else if (this.form && _.get(changes, 'disabled') && !changes.disabled.currentValue) {
      this.form.enable()
    }
  }
  
  initForm() {
    this.form = this.fb.group({
      city: [null, Validators.required]
    })

    if(this.previousData) {
      let cityObj = {
        id: _.get(this.previousData, 'city.id') || null,
        province: _.get(this.previousData, 'province') || null,
        description: _.get(this.previousData, 'city.description') || null,
      }
      this.form.get('city').setValue(cityObj, { emitEvent: true });
      this.currentFilters.web_filter.keyword = _.get(this.previousData, 'city.description');
      // if (this.cascadeFilters) {
      //   this.currentFilters.web_filter.region_ids = _.get(this.cascadeFilters, 'region_ids')
      // }
      this.getListData('edit');
    }
  }

  detectFormChanges() {
    this.form.get('city').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.currentFilters.web_filter.keyword = _.toString(value);
      this.listData = [];
      // if (this.cascadeFilters) {
      //   this.currentFilters.web_filter.region_ids = _.get(this.cascadeFilters, 'region_ids')
      // }
      this.getListData(null);
    });
  }

  getInitialValues(data) {
    this.currentFilters.web_filter.offset = 0;
    this.currentFilters.web_filter.keyword = "";
    this.listData = [];
    this.getListData(null);
  }
   
  getMoreData(name) {
    if (this.currentFilters.getMore) {
      this.currentFilters.web_filter.offset += requestCity.web_filter.limit;
      this.getListData(name);
    }
  }

  getListData(mode) {
    this.loading.city = true;
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }

    this.listSub = this.campSvc.getCitiesForDropdownComponent(this.currentFilters).pipe(
      finalize(() => {
        this.listSub.unsubscribe();
        return observableOf(null);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        this.loading.city = false;
        this.loading.init = false;
        this.loading.more = false;
        if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
          this.loading.error = true;
          this.listData = [];
          this.dialogService.openSnackBar(this.networkErrMsg)
          if(mode == 'edit') { 
            this.errorLoading.next(true);
          }
          return;
        }
        
        if (_.has(res, 'value') && (res.value == "Success")) {
          this.listData = this.listData.concat(res.data);

          if((res.data.length > 0) && (this.listData.length < _.get(res, 'count'))) {
            this.currentFilters.getMore = true;
          }
        }

        if((_.toLower(_.get(res, 'value')) == "empty object") && (this.currentFilters.getMore == true)) {
          this.currentFilters.getMore = false;
        }

      }, err => {
        if (environment.showLogs) {
          console.error(err);
        }
        if(mode == 'edit') { 
          this.errorLoading.next(true);
        }
        this.loading.city = false;
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.limit -= this.currentFilters.web_filter.offset;
        }
        return observableOf(null);
      });
  }

  filterListdata() {
    let cityObj = {};
    cityObj = this.listData.filter(e => {
      return e.id == _.get(this.previousData, 'city.id')
    })
    this.form.get('city').setValue(cityObj[0]);
    this.autoCmpltClosed();
  }

  displayFn(data?: any): string | undefined {
    if(data) {
      return _.get(data, 'code') != "all" ? (_.get(data, 'province.description') + ', ' + _.get(data, 'description')) : _.get(data, 'description');
    }
    return null;
  }

  autoCmpltClosed() {
    this.listData = [];

    if (_.isObject(_.get(this.form.get('city'), 'value'))) {
      this.optionSelected.next(_.get(this.form.get('city'), 'value'))
    }
  }

  clearForm(ev: Event) {
    this.form.get('city').setValue(null, { emitEvent: false });
    this.optionSelected.next(null)
    // ev.stopPropagation();
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  checkAutocompleteObject(control, objectKey) {
    if ((!_.isObject(control.controls[objectKey].value) || control.controls[objectKey].value.id == null) && (control.controls[objectKey].value && control.controls[objectKey].value != "")) {
      control.controls[objectKey].setErrors({
        "notExisting": true
      });

      this.optionSelected.next(null)
    }
  }
}